// src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import { Spinner } from "../components/UI";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, phone) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await setDoc(doc(db, "businesses", result.user.uid), {
      businessName:   "",
      email,
      phone:          phone || "",
      reviewLink:     "",
      reviewPlatform: "google",
      twilioNumber:   "",
      createdAt:      serverTimestamp(),
      plan:           "free",
    });
    return result;
  }

  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function resetPassword(email) {
    // Same branded page + handleCodeInApp as the employee invite flow in
    // functions/employees.js, so both paths land somewhere that actually
    // looks like this app instead of Firebase's raw default template.
    return sendPasswordResetEmail(auth, email, {
      url: "https://app.techbugz.com/reset-password",
      handleCodeInApp: true,
    });
  }

  // First-time Google sign-in acts as signup (creates the businesses doc,
  // same shape as the email/password path); a returning Google user just
  // signs in against their existing doc, untouched.
  async function loginWithGoogle() {
    const result = await signInWithPopup(auth, new GoogleAuthProvider());
    const ref = doc(db, "businesses", result.user.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        businessName:   "",
        email:          result.user.email || "",
        phone:          "",
        reviewLink:     "",
        reviewPlatform: "google",
        twilioNumber:   "",
        createdAt:      serverTimestamp(),
        plan:           "free",
      });
    }
    return result;
  }

  async function logout() {
    return signOut(auth);
  }

  async function fetchBusinessProfile(uid) {
    const snap = await getDoc(doc(db, "businesses", uid));
    if (snap.exists()) {
      const data = snap.data();
      // Onboarding used to save the Maps link as `mapLink`, a name nothing
      // in the app reads (Settings and Reviews use googleMapsLink). Fall
      // back to it so clients who already onboarded don't have to re-enter
      // it — the next Settings save then writes it under the right name.
      if (!data.googleMapsLink && data.mapLink) data.googleMapsLink = data.mapLink;
      setBusinessProfile({ id: snap.id, ...data });
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchBusinessProfile(user.uid);
      } else {
        setBusinessProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    businessProfile,
    signup,
    login,
    loginWithGoogle,
    resetPassword,
    logout,
    fetchBusinessProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
          <Spinner size={32} />
        </div>
      ) : children}
    </AuthContext.Provider>
  );
}